<template>
  <div
    :class="{ disabled: disabled }"
    class="app-lazy-select"
  >
    <div class="app-select-wrapper">
      <div class="d-flex align-items-end justify-content-between">
        <div
          class="app-members-selector-title"
          @click.prevent="toggleContent"
        >
          <template v-if="label">
            <label class="control-label">{{ label }}</label>
            <i
              v-if="disabled"
              v-tooltip
              class="hi hi-info-filled hi-12 a-tooltip"
              :title="disabledTooltip"
            />
            <i
              v-if="tooltipInfo"
              v-tooltip
              class="hi-info hi-12 text-lighter a-tooltip"
              :title="tooltipInfo"
            />
            <slot name="after-label" />
          </template>

          <div
            v-if="description"
            class="label-secondary"
          >
            <span>{{ description }}</span>
          </div>
        </div>
      </div>

      <div class="app-select">
        <div
          class="select2 select2-container select2-container--bootstrap"
          data-select2-ignore
        >
          <button
            v-if="showClearAllTrigger"
            class="clear-all-trigger d-flex align-items-center justify-content-center text-muted px-15 py-10"
            data-testid="lazy-select-clear-all"
            @click.stop="onClearAll()"
          >
            <i class="hi hi-close" />
          </button>

          <div
            ref="anchor"
            class="selection"
            @click="toggleContent"
          >
            <div
              v-if="selectedCount"
              class="select2-selection-counter d-flex align-items-center"
              data-testid="selection-counter"
            >
              <template v-if="multiple"> {{ selectedCount }} {{ selectedLabel }} </template>
              <template v-else>
                <div class="d-flex align-items-center w-full">
                  <slot
                    name="single-result-leading"
                    :value="value"
                  />
                  <p>{{ value?.name }}</p>
                </div>
              </template>
            </div>
            <div
              v-else
              class="select2-selection select2-selection-ready select2-selection-fake"
              :class="{ 'select2-selection-fake-disabled': disabled }"
            >
              <slot name="placeholder">Select {{ labelPlural }}</slot>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Teleport
      to="body"
      :disabled="!teleportToBody"
    >
      <div
        v-show="showContent"
        ref="content"
        class="lazy-select-content px-0"
        :class="{ fixed: appendToBody, absolute: !appendToBody }"
        data-testid="lazy-select-content"
      >
        <div class="lazy-select-content-header d-flex flex-column pt-15">
          <!-- TODO: Implement filters slider -->
          <div
            v-if="state.filters.length"
            class="lazy-select-content-filters d-flex align-items-center"
          >
            <div
              class="lazy-select-content-filters-inner d-flex align-items-center"
              :class="{ overflown: false }"
            >
              <button
                type="button"
                class="lazy-select-content-filters-inner-left"
              >
                <i class="hi hi-12 hi-left text-muted" />
              </button>

              <button
                type="button"
                class="lazy-select-content-filters-inner-right"
              >
                <i class="hi hi-12 hi-left text-muted" />
              </button>

              <div
                v-for="(filter, filterIndex) in state.filters"
                :key="`${filter.id}-${filterIndex}`"
                class="lazy-select-content-filters-item d-flex align-items-center justify-content-center gap-10"
                :class="{ selected: filter.selected }"
                @click.prevent.stop="onFilterSelected(filter, filterIndex)"
              >
                {{ filter.label }}
                <i
                  v-if="filter.selected"
                  class="hi hi-check text-blue"
                />
              </div>
            </div>
          </div>

          <AppInputSearch
            class="my-0"
            show-reset-button
            @reset="onSearchChange()"
          >
            <AppInput
              ref="searchInput"
              type="search"
              data-testid="lazy-select-search"
              :placeholder="searchPlaceholder"
              :value="state.search"
              @input="onSearchChange"
              @keyup.enter.native="onSearchEnter"
            />
          </AppInputSearch>
        </div>

        <div
          ref="results"
          class="lazy-select-content-results"
          :class="{ loading: state.loading }"
        >
          <div
            v-if="state.loading && !state.items.length"
            class="px-15 no-results my-20"
          >
            Loading, please wait...
          </div>
          <template v-else-if="state.items.length">
            <VirtualList
              ref="resultsList"
              class="lazy-select-content-results-list"
              data-key="id"
              :data-component="{}"
              :data-sources="state.items"
              :estimate-size="45"
              :bottom-threshold="45 * 5"
              @tobottom="onScrollBottom"
            >
              <template #header>
                <div
                  v-if="multiple"
                  class="lazy-select-content-results-list-item select-all-trigger"
                  data-testid="select-all-trigger"
                  :class="{ disabled: disableSelectAll }"
                  @click="!disableSelectAll && onSelectAll()"
                >
                  <label class="custom-checkbox-wrapper text-blue">
                    <strong>Select all</strong>
                  </label>
                </div>
              </template>

              <template #item="{ item }">
                <AppLazySelectItem
                  :item="item"
                  :loading="state.loading"
                  :multiple="multiple"
                  :selected="isItemSelected(item)"
                  :disabled="isItemDisabled(item)"
                  :highlight="firstItem?.id === item.id && !!state.search"
                  @input="onItemSelect($event, { applyAfterSelect: true })"
                >
                  <div
                    v-tooltip
                    v-bind="{
                      ...(isItemDisabled(item) &&
                        typeof item.disabled === 'string' && { 'data-original-title': item.disabled }),
                    }"
                    class="lazy-select-content-results-list-item-content d-flex align-items-center w-full pr-10"
                  >
                    <slot
                      name="item-leading"
                      :item="item"
                      :loading="state.loading"
                    />
                    <div class="item-contents-wrapper">
                      <p
                        class="lazy-select-content-results-list-item-content-name"
                        data-testid="select-item-name"
                      >
                        {{ item.name }}
                      </p>
                      <div
                        v-if="$scopedSlots.itemSecondary || $scopedSlots['item-secondary']"
                        class="item-secondary"
                      >
                        <slot
                          name="item-secondary"
                          :item="item"
                          :loading="state.loading"
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="$scopedSlots.itemTrailing || $scopedSlots['item-trailing']"
                    class="item-trailing"
                  >
                    <slot
                      name="item-trailing"
                      :item="item"
                      :loading="state.loading"
                    />
                  </div>
                </AppLazySelectItem>
              </template>
            </VirtualList>
          </template>
          <div
            v-else
            class="empty-results d-flex flex-column align-items-center justify-content-center height p-20"
          >
            <img
              :src="IMAGE_EMPTY_BOX"
              :alt="`No ${labelPlural} found.`"
            />
            <div class="d-flex flex-column align-items-center gap-5">
              <h6 class="m-0">No {{ labelPlural }} found</h6>
            </div>
          </div>
        </div>

        <!-- TODO: Remove me once we have sections/grouping feature where we pull the selected items to the top -->
        <div
          v-if="multiple"
          class="lazy-select-content-footer d-flex align-items-center px-15 py-10"
        >
          <AppOnOffToggle
            class="text-muted"
            :value="state.selectedOnly"
            :disabled="disableSelectedOnlyToggle"
            @input="toggleSelectedOnly"
          >
            Show only selected
          </AppOnOffToggle>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
// @ts-check
import Vue, { defineComponent } from 'vue';
import VirtualList from 'vue-virtual-scroll-list';
import { v4 as uuidv4 } from 'uuid';
import { isPlainObject, throttle } from 'lodash-es';
import { IMAGE_EMPTY_BOX } from '~/rails_assets';
import { pluralize } from '~/utils/string_utils';
import { size, shift, offset, autoPlacement, autoUpdate, computePosition } from '@floating-ui/dom';
import { DIALOG_OPEN_WIN_EVENT, LAZY_OPEN_WIN_EVENT, SIDEBAR_OPEN_WIN_EVENT } from '~/app_constants';
import Teleport from 'vue2-teleport';

/**
 * @typedef {Object} Pagination
 * @prop {number | null} next_page
 * @prop {number | null} prev_page
 * @prop {boolean} first_page
 * @prop {boolean} last_page
 */

/**
 * @typedef {number} Id
 */

/**
 * @typedef {Record<string, any>} Item
 * @prop {Id} id
 * @prop {boolean} selected
 * @prop {boolean | string} disabled
 */

/**
 * @typedef {'NORMAL' | 'SELECT_ALL'} SelectionType
 */

/**
 * @typedef {Item[]} SelectModel
 */

/**
 * @typedef {Object} FetcherResponse
 * @prop {Item[]} items
 * @prop {Pagination} pagination
 * @prop {Id[]} selected_ids
 * @prop {Id[]} unselected_ids
 * @prop {Id[]} disabled_ids
 * @prop {Id[]} selected_disabled_ids
 */

/**
 * @typedef {Object} Filter
 * @prop {string} id
 * @prop {string} label
 * @prop {any} value
 * @prop {boolean?} root
 * @prop {boolean} multiple
 * @prop {boolean} selected
 */

const EVENTS = {
  OPEN: 'open',
  CLOSE: 'close',
  INPUT: 'input',
};

export default defineComponent({
  name: 'AppLazySelect',
  components: {
    VirtualList,
    Teleport,
  },
  props: {
    // Text used for the label of the input.
    label: {
      type: String,
      default: null,
    },
    // Allows adding extra information/context to the input as a sublabel.
    // i.e:
    // ```
    //   MEMBERS: <= LABEL
    //   This are the users that can manage the project.  <= DESCRIPTION
    // ```
    description: {
      type: String,
      default: null,
    },
    // What type of resource are we dealing with? (Member, Project, etc).
    resource: {
      type: String,
      default: 'item',
    },
    // Optional explicit labels to avoid naive pluralization on multi-word resources.
    // If provided, these will be used instead of `resource` + `pluralize`.
    resourceLabel: {
      type: String,
      default: null,
    },
    resourceLabelPlural: {
      type: String,
      default: null,
    },
    // The resource id.
    resourceId: {
      type: Number,
      default: null,
    },
    // Prop to be used as `v-model` or by itself and to keep in sync.
    //
    // * If "single select" it will be the actual object item.
    // * If "multiple select" it will be an array of ids for the selected items.
    //
    // We do this since when this is a `multiple` select, it can be that the
    // values are really huge, and thus we might be holding on to too much
    // memory because we are tracking a lot of objects internally and
    // synced with the `v-model` attribute.
    value: {
      type: [Array, Object],
      /** @ts-ignore */
      default(props) {
        if (props?.asFilter) {
          return props?.multiple ? { selectAll: false, items: [] } : null;
        }

        return props?.multiple ? [] : null;
      },
      /** @ts-ignore */
      validator(value) {
        // Valid `value`s:
        // * null: The field it's still empty
        // * object: The field it's single select and has to contain an `id` field
        // * array: The field it's multiple select and it's empty or it's not empty and it's has only `numbers`
        return (
          value === null ||
          /** @ts-ignore */
          (isPlainObject(value) &&
            ['number', 'string'].includes(typeof value?.id) &&
            typeof value?.name === 'string') ||
          (Array.isArray(value) && (value.length === 0 || ['number', 'string'].includes(typeof value[0]))) ||
          // `as-filter`
          /** @ts-ignore */
          (isPlainObject(value) && typeof value?.selectAll === 'boolean' && Array.isArray(value?.items))
        );
      },
    },
    asFilter: {
      type: Boolean,
      default: false,
    },
    // Whether the component should trigger the `input` event eagerly
    // when selecting options instead of on `blur/close`.
    //
    // NOTE: This only applies when the `multiple = true`.
    eager: {
      type: Boolean,
      default: false,
    },
    // Whether or not to append the "floating content" to the body instead of leaving it in the same container.
    appendToBody: {
      type: Boolean,
      default: false,
    },
    // Whether to teleport the "floating content" to the body on open.
    teleportToBody: {
      type: Boolean,
      default: false,
    },
    // Whether or not this component should be disabled from user interaction.
    disabled: {
      type: Boolean,
      default: false,
    },
    // If disabled, use this text as tooltip.
    disabledTooltip: {
      type: String,
      default: null,
    },
    // Some optional message to display besides the selection.
    tooltipInfo: {
      type: String,
      default: '',
    },
    // Whether or not it's a select with multiple options functionality.
    multiple: {
      type: Boolean,
      default: false,
    },
    // Boolean to override check on this.multiple to allow watcher on .value to set selected and unselected ids.
    setStateOnValueChange: {
      type: Boolean,
      default: false,
    },
    // Fit withing parent element width
    fitParent: {
      type: Boolean,
      default: false,
    },
    // Function that will be used to fetch the items from the backend; it needs to implement the following contract:
    //   (payload: Record<string, any>) => Promise<FetcherResponse>
    //
    // It has this shape because we want to allow flexibility when customizing the `queryParams` from the outside
    // while also avoiding prop-drilling within the specialized components that wrap `AppLazySelect`.
    fetcher: {
      type: Function,
      required: true,
    },
    // Filters to use within the selector, these have to have the shape of:
    // `[{ id: 'filterId', value: 'filterValue', label: 'Filter label', root: true|false }]`
    //
    // * id: Used to identify the filter that will be sent to the backend.
    // * value: Will be sent to the backend together with the `id`.
    // * label: Text to display in the UI.
    // * multiple: Boolean that tells the component that we could have multiple
    //             values applied to same filter.
    // * root: Boolean indicating that "clicking/selecting" this filter will reset the rest
    //         of the filters; there should only be just one.
    filters: {
      type: Array,
      default: () => [],
    },
    // Whether to render disabled items as visually selected when their id is in selectedIds.
    showSelectedStateForDisabledItems: {
      type: Boolean,
      default: false,
    },
    minWidth: {
      type: Number,
      default: 340,
    },
    removeClearAllTrigger: {
      type: Boolean,
      default: false,
    },
    // Whether or not to preload the items when the component is mounted.
    preload: {
      type: Boolean,
      default: false,
    },
    // meant to mark the member ids that are not removed when clicking on the "x" (Clear all)
    unclearableIds: {
      type: Array,
      default: () => [],
    },
    // Whether or not to ignore the window events.
    // Used when we will redirect the user to the new page after selecting the item by clicking outside the selector.
    ignoreWindowEvents: {
      type: Boolean,
      default: false,
    },
    // Whether it should show only items that are not part of the initial value.
    // This is useful when we want to show only items that are not selected yet (e.g. adding new members to a team)
    unselectedOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    /** @type {Filter[]} */
    const filters = [];

    if (this.filters.length) {
      for (const filter of /** @type {Filter[]} */ (/** @type {unknown} */ this.filters)) {
        filters.push({
          ...filter,
          selected: filter.selected ?? false,
          multiple: filter.multiple ?? false,
        });
      }
    }

    // @ts-ignore
    const state = this.prepareDerivedDataFromValue(this.value);

    return {
      uuid: uuidv4(),

      // --------------
      // UI state
      // --------------
      opening: false,
      IMAGE_EMPTY_BOX,
      showContent: false,
      isSelectAll: state.selectionType === 'SELECT_ALL' && !state.unselectedIds.size,

      // --------------
      // Business Logic state
      // --------------
      state: {
        /** @type {Boolean} */
        selectedOnly: false,
        /** @type {Boolean} */
        loading: false,
        /** @type {string} */
        search: '',
        /** @type {Pagination | null} */
        pagination: null,
        /** @type {number} */
        currentPage: 1,
        /** @type {SelectionType} */
        selectionType: state.selectionType,
        /** @type {Item[]} */
        items: [],
        /** @type {Record<Id, Item>} */
        changed: state.changed,
        /** @type {Set<Id>} */
        selectedIds: state.selectedIds,
        /** @type {Set<Id>} */
        unselectedIds: state.unselectedIds,
        /** @type {Set<Id>} */
        disabledIds: new Set(),
        /** @type {Set<Id>} */
        selectedDisabledIds: new Set(),
        /** @type {Set<Id>} */
        prevSelectedIds: state.prevSelectedIds,
        /** @type {Set<Id>} */
        initiallySelectedIds: new Set(state.selectedIds),
        /** @type {Filter[]} */
        filters,
      },
    };
  },
  computed: {
    // --------------
    // UI Computed
    // --------------
    searchPlaceholder() {
      return `Search ${this.labelPlural}`;
    },

    selectedCount() {
      if (this.unselectedOnly) {
        return this.newlyAdded.length;
      }

      return this.state.selectedIds.size;
    },

    // Derived singular/plural labels based on props and current selection
    labelSingular() {
      return this.resourceLabel || this.resource;
    },

    labelPlural() {
      // Prefer explicit plural label when provided via props to avoid naive pluralization
      if (this.$props.resourceLabelPlural) {
        return this.$props.resourceLabelPlural;
      }

      return pluralize(2, this.labelSingular);
    },

    // Use singular vs plural label based on the current count
    selectedLabel() {
      return this.selectedCount === 1 ? this.labelSingular : this.labelPlural;
    },

    disableSelectAll() {
      return this.disabled || this.isSelectAll;
    },

    disableSelectedOnlyToggle() {
      return this.disabled || (!this.selectedCount && !this.state.selectedOnly);
    },

    showClearAllTrigger() {
      if (this.removeClearAllTrigger || !this.multiple) {
        return false;
      }

      if (this.asFilter) {
        return this.value?.selectAll || this.value?.items.length;
      }

      if (this.unselectedOnly) {
        return this.newlyAdded.length > 0;
      }

      return this.value.length > 0;
    },

    isEager() {
      return this.multiple && this.eager;
    },

    // --------------
    // Business Logic Computed
    // --------------

    /**
     * Compute the difference between the 2 sets of "selected ids" (new & old) to determine if in fact
     * the user has performed any changes to the selection since the last time the select was opened.
     */
    isDirty() {
      // If it's single select, we don't need to compute the entire difference, just check if
      // the `value.id` is present in the `selectedIds`.
      if (!this.multiple) {
        /** @ts-ignore */
        return !this.state.selectedIds.has(this.value?.id);
      }

      // If it's `multiple` select, first try and short-circuit by checking the length of both:
      // the `value` and the `selectedIds`.
      if (this.value.length !== this.state.selectedIds.size) {
        return true;
      }

      // Compute the difference between the two sets.
      const isCurrentIdsSizeBigger = this.state.selectedIds.size > this.state.prevSelectedIds.size;
      /** @type {Set<number>} */
      const diff = isCurrentIdsSizeBigger ? new Set(this.state.selectedIds) : new Set(this.state.prevSelectedIds);
      const setToIterate = isCurrentIdsSizeBigger ? this.state.prevSelectedIds : this.state.selectedIds;
      const setToCheck = isCurrentIdsSizeBigger ? this.state.selectedIds : this.state.prevSelectedIds;

      for (const item of setToIterate.keys()) {
        if (setToCheck.has(item)) {
          diff.delete(item);
        }
      }

      return !!diff.size;
    },

    /**
     * @returns {Item}
     */
    firstItem() {
      const [firstItem] = this.state.items;

      return firstItem;
    },

    newlyAdded() {
      if (!this.multiple) {
        return [];
      }

      return [...this.state.selectedIds].filter((id) => !this.state.initiallySelectedIds.has(id));
    },
  },
  watch: {
    value(newValue) {
      if (!this.multiple || this.setStateOnValueChange) {
        const { selectedIds, prevSelectedIds } = this.prepareDerivedDataFromValue(newValue);

        Vue.set(this.state, 'selectedIds', selectedIds);
        Vue.set(this.state, 'prevSelectedIds', prevSelectedIds);
      }
    },
  },
  created() {
    // We use throttle here because we want to make sure that the request it's performed
    // when the `:bottom-threshold` is first reached.
    this.onScrollBottom = throttle(this.onScrollBottom.bind(this), 250);
    this.onWindowSidebarDialogOpen = this.onWindowSidebarDialogOpen.bind(this);
    this.onWindowLazySelectOpen = this.onWindowLazySelectOpen.bind(this);

    if (!this.ignoreWindowEvents) {
      window.addEventListener(DIALOG_OPEN_WIN_EVENT, this.onWindowLazySelectOpen);
      window.addEventListener(LAZY_OPEN_WIN_EVENT, this.onWindowLazySelectOpen);
      window.addEventListener(SIDEBAR_OPEN_WIN_EVENT, this.onWindowSidebarDialogOpen);
    }
    if (this.preload) {
      this.fetchItems();
    }
  },
  async mounted() {
    if (this.appendToBody && this.getContentRef()) {
      document.body.appendChild(this.getContentRef());
    }
  },
  beforeDestroy() {
    if (this.cleanContentUpdate) {
      this.cleanContentUpdate();
      this.cleanContentUpdate = null;
    }

    if (this.appendToBody && this.getContentRef()) {
      document.body.removeChild(this.getContentRef());
    }

    if (!this.ignoreWindowEvents) {
      window.removeEventListener(LAZY_OPEN_WIN_EVENT, this.onWindowLazySelectOpen);
      window.removeEventListener(SIDEBAR_OPEN_WIN_EVENT, this.onWindowSidebarDialogOpen);
    }
  },
  methods: {
    // --------------
    // UI Methods
    // --------------
    pluralize,

    /**
     * @param {Item} item
     */
    isItemSelected(item) {
      if (this.showSelectedStateForDisabledItems) {
        return this.state.selectedIds.has(item.id);
      }

      return this.state.selectedIds.has(item.id) && !(!!item.disabled || this.state.disabledIds.has(item.id));
    },

    /**
     * @param {Item} item
     */
    isItemDisabled(item) {
      return !!item.disabled || this.state.disabledIds.has(item.id) || this.state.selectedDisabledIds.has(item.id);
    },

    /**
     * @param {Set<Id>} selectedIds
     * @returns {Set<Id>}
     */
    getSelectAll(selectedIds) {
      selectedIds = selectedIds || this.state.selectedIds;
      const previousSelectedIds = this.state.selectedIds;
      const unclearableIds = [...this.state.selectedDisabledIds].filter((id) => !previousSelectedIds.has(id));

      return new Set([...selectedIds].filter((id) => !unclearableIds.includes(id)));
    },

    /**
     * @param {Set<Id>} allItems
     * @param {Set<Id>} selectedIds
     * @returns {Set<Id>}
     */
    getUnselected(allItems, selectedIds) {
      selectedIds = selectedIds || this.state.unselectedIds;

      return new Set([...allItems].filter((id) => !selectedIds.has(id)));
    },

    /**
     @returns {Set<Id>}
     */
    getUnclearableSelected() {
      return new Set([...this.state.selectedIds].filter((id) => this.unclearableIds.includes(id)));
    },

    // @ts-ignore
    onWindowLazySelectOpen(event) {
      if (event.type === DIALOG_OPEN_WIN_EVENT) {
        this.closeContent();

        return;
      }

      if (event.detail?.uuid !== this.uuid) {
        this.closeOrApply();
      }
    },

    onWindowSidebarDialogOpen() {
      this.closeOrApply();
    },

    /**
     * @param {Boolean} newValue
     */
    async toggleSelectedOnly(newValue) {
      this.state.selectedOnly = newValue;
      await this.onPageChange(1);
    },

    async toggleContent() {
      if (this.showContent) {
        this.closeOrApply();
      } else {
        await this.openContent();
      }
    },

    async openContent() {
      this.showContent = true;
      this.opening = true;
      window.dispatchEvent(
        new CustomEvent(LAZY_OPEN_WIN_EVENT, {
          detail: { uuid: this.uuid },
        }),
      );
      this.mountContent();
      this.$emit(EVENTS.OPEN);
      await this.fetchItems();
      this.getSearchRef()?.focus();

      this.$nextTick(() => {
        const resultsList = this.$refs.resultsList;

        if (resultsList?.scrollToOffset) {
          resultsList.scrollToOffset(0);
        }
      });

      this.opening = false;
    },

    async mountContent() {
      const content = this.getContentRef();

      if (!content) {
        return;
      }

      const anchor = this.getAnchorRef();
      const controller = new AbortController();

      document.addEventListener(
        'click',
        (event) => {
          /** @ts-ignore */
          if (this.opening || (event.target && content.contains(event.target))) {
            return;
          }

          this.closeOrApply();
          controller.abort();
        },
        { signal: controller.signal },
      );

      // Reset the results height style in case we had to adapt it.
      // @ts-ignore
      this.$refs.results.removeAttribute('style');

      this.cleanContentUpdate = autoUpdate(anchor, content, async () => {
        const { x, y } = await computePosition(anchor, content, {
          placement: 'bottom',
          strategy: this.appendToBody ? 'fixed' : 'absolute',
          middleware: [
            offset({ mainAxis: 5 }),
            // By using `autoPlacement` instead of the `flip` middleware, we make sure
            // that the "space optimization" is conservative. By this we mean that
            // it will always try and use the placement where more space it's available
            // and not change it, unless extremely necessary.
            //
            // See: https://floating-ui.com/docs/autoPlacement#conflict-with-flip
            autoPlacement({
              crossAxis: false,
              allowedPlacements: ['top-start', 'top-end', 'bottom-start', 'bottom-end'],
            }),
            shift(),
            size({
              apply: ({ rects, elements, availableHeight }) => {
                const maxWidth = `${Math.max(
                  rects.reference.width,
                  this.fitParent ? rects.reference.width : this.minWidth,
                )}px`;

                Object.assign(elements.floating.style, { maxWidth });

                // This allows us to "substract" the correct max height off the `results` scrolling area.
                if (availableHeight < rects.floating.height) {
                  // @ts-ignore
                  const { height: currentResultsHeight } = this.$refs.results.getBoundingClientRect();
                  const maxHeightDiff = rects.floating.height - availableHeight;

                  // @ts-ignore
                  Object.assign(this.$refs.results.style, {
                    maxHeight: `${currentResultsHeight - maxHeightDiff}px`,
                  });
                }
              },
            }),
          ],
        });

        Object.assign(content.style, {
          top: `${y}px`,
          left: `${x}px`,
        });
      });
    },

    /**
     * @returns {HTMLInputElement}
     */
    getSearchRef() {
      const searchInput = /** @type {Vue} */ (/** @type {unknown} */ this.$refs.searchInput);
      const element = /** @type {HTMLInputElement} */ (/** @type {unknown} */ searchInput.$el);

      return element;
    },

    /**
     * @returns {HTMLDivElement}
     */
    getAnchorRef() {
      return /** @type {HTMLDivElement} */ (/** @type {unknown} */ this.$refs.anchor);
    },

    /**
     * @returns {HTMLDivElement}
     */
    getContentRef() {
      return /** @type {HTMLDivElement} */ (/** @type {unknown} */ this.$refs.content);
    },

    closeContent() {
      this.showContent = false;
      this.state.selectedOnly = false;
      this.resetFilters();
      this.state.search = '';
      Vue.set(this.state, 'selectedIds', new Set(this.state.prevSelectedIds));
      this.$emit(EVENTS.CLOSE);
      if (this.cleanContentUpdate) {
        this.cleanContentUpdate();
      }
    },

    apply() {
      this.showContent = false;
      this.state.selectedOnly = false;
      if (!this.isEager) {
        this.updateModelInput();
      }
      if (this.cleanContentUpdate) {
        this.cleanContentUpdate();
      }
      this.reset();
    },

    closeOrApply() {
      if (this.isDirty) {
        this.apply();
      } else {
        this.closeContent();
      }
    },

    updateModelInput() {
      if (this.multiple) {
        if (this.asFilter) {
          const selectAll = this.state.selectionType === 'SELECT_ALL';
          const items = selectAll ? this.state.unselectedIds : this.state.selectedIds;

          this.$emit(EVENTS.INPUT, { selectAll, items: [...items.values()] });
        } else if (this.unselectedOnly) {
          this.$emit(EVENTS.INPUT, this.newlyAdded);
        } else {
          this.$emit(EVENTS.INPUT, [...this.state.selectedIds.values()]);
        }
      } else {
        const [item] = [...Object.values(this.state.changed)];

        this.$emit(EVENTS.INPUT, item ?? null);
      }
    },

    /**
     * @param {number} page
     */
    async onPageChange(page) {
      this.state.currentPage = page;
      await this.fetchItems();
    },

    async onSearchChange(newValue = '', fromEnter = false) {
      if (!fromEnter) {
        this.state.search = newValue;
        // Reset the page after changing search
        await this.onPageChange(1);
      }
    },

    async onSearchEnter() {
      if (this.state.search && this.firstItem && !this.state.loading) {
        this.onItemSelect(
          { ...this.firstItem, selected: this.isItemSelected(this.firstItem) },
          { applyAfterSelect: true },
        );
        // Select the input for a better UX. This way the user can quickly remove the text
        this.getSearchRef().focus();
        this.getSearchRef().select();
      }
    },

    resetFilters() {
      if (this.state.filters.length) {
        for (let i = 0; i < this.state.filters.length; i++) {
          Vue.set(this.state.filters[i], 'selected', !!this.filters[i].selected);
        }
      }
    },

    /**
     * @param {Filter} filter
     * @param {number} filterIndex
     */
    async onFilterSelected(filter, filterIndex) {
      if (filter.root) {
        this.resetFilters();
      } else {
        for (let i = 0; i < this.state.filters.length; i++) {
          // Only reset those that are:
          // * Not the clicked filter
          // * That have the same `id`
          // * That don't hold "multiple" values
          if (i !== filterIndex && this.state.filters[i].id === filter.id && !filter.multiple) {
            Vue.set(this.state.filters[i], 'selected', false);
          }
        }

        Vue.set(this.state.filters[filterIndex], 'selected', !filter.selected);
      }

      await this.onPageChange(1);
    },

    onScrollBottom() {
      if (!this.state.loading && !this.state.pagination?.last_page) {
        // @ts-ignore
        this.onPageChange(this.state.pagination.next_page);
      }
    },

    // --------------
    // Business Logic Methods
    // --------------

    // @ts-ignore
    prepareDerivedDataFromValue(value) {
      let selectedIds, unselectedIds;
      /** @type {SelectionType} */
      const selectionType = this.asFilter && value.selectAll ? 'SELECT_ALL' : 'NORMAL';

      if (this.asFilter && this.multiple && selectionType === 'SELECT_ALL') {
        selectedIds = new Set();
        unselectedIds = new Set(value.items);
      } else {
        selectedIds = this.multiple
          ? new Set(this.asFilter ? value?.items ?? [] : value)
          : new Set([value?.id].filter(Boolean));
        unselectedIds = new Set();
      }

      const prevSelectedIds = new Set(selectedIds);
      /** @type {Record<Id, Item>} */
      const changed = this.multiple ? {} : value?.id ? { [value.id]: { ...value } } : {};

      return { selectedIds, unselectedIds, prevSelectedIds, changed, selectionType };
    },

    /**
     * @param {Item} item
     * @param {Object} options
     * @param {boolean} options.applyAfterSelect
     */
    onItemSelect(item, options = { applyAfterSelect: false }) {
      // Avoid processing items that are `disabled`
      if (this.isItemDisabled(item)) {
        return;
      }

      if (this.multiple) {
        const selectedIds = new Set(this.state.selectedIds);
        const unselectedIds = new Set(this.state.unselectedIds);
        const newSelected = !item.selected;

        // If we're moving an item to unselected when we're in `select all` mode
        // just flip the `isSelectAll` flag so that we can enable the toggle.
        if (!newSelected && this.isSelectAll) {
          this.isSelectAll = false;
        }

        selectedIds[item.selected ? 'delete' : 'add'](item.id);
        unselectedIds[item.selected ? 'add' : 'delete'](item.id);
        Vue.set(this.state, 'selectedIds', selectedIds);
        Vue.set(this.state, 'unselectedIds', unselectedIds);
        Vue.set(this.state.changed, item.id, { ...item, selected: newSelected });

        if (!this.state.selectedIds.size && this.state.selectedOnly) {
          this.toggleSelectedOnly(false);
        }

        if (!this.state.selectedIds.size) {
          this.isSelectAll = false;
          this.state.selectionType = 'NORMAL';
        }

        if (this.isEager) {
          this.updateModelInput();
        }
      } else {
        // We don't allow to remove single selections, user must select an item
        // We'are checking `!item.selected` because when the user tries to unselect
        // an item, this value will be `false`.
        if (!item.selected) {
          Vue.set(this.state, 'selectedIds', new Set([item.id]));
          Vue.set(this.state, 'changed', { [item.id]: { ...item, selected: true } });

          if (options.applyAfterSelect) {
            this.apply();
          }
        }
      }
    },

    reset() {
      this.state.search = '';
      this.state.selectedOnly = false;
      if (!this.asFilter) {
        this.state.selectionType = 'NORMAL';
      }
      this.state.loading = false;
      this.state.changed = {};
      this.state.currentPage = 1;
    },

    async onSelectAll() {
      this.isSelectAll = true;
      this.state.changed = {};
      this.state.selectionType = 'SELECT_ALL';
      if (this.asFilter) {
        Vue.set(this.state, 'unselectedIds', new Set());
      }
      await this.fetchItems();
      this.updateModelInput();
    },

    async onClearAll() {
      this.isSelectAll = false;
      this.state.changed = {};
      this.state.selectionType = 'NORMAL';
      const selectedIds = this.getUnclearableSelected();

      Vue.set(this.state, 'selectedIds', new Set(selectedIds));
      Vue.set(this.state, 'unselectedIds', new Set());
      if (this.asFilter) {
        await this.fetchItems();
      }
      if (this.isEager) {
        this.updateModelInput();
      }
      this.apply();
    },

    /**
     * @param {Id[]} itemsIds
     */
    async excludeItems(itemsIds = []) {
      const selectedIds = new Set(this.state.selectedIds);

      // Only trigger the logic if we really have items already selected in the state
      if (selectedIds.size) {
        this.isSelectAll = false;

        // Make sure that we set the correct status on the items,
        for (const itemId of itemsIds) {
          if (this.state.changed[itemId] || selectedIds.has(itemId)) {
            const changedItem = this.state.changed[itemId];

            this.state.changed[itemId] = { ...(changedItem ?? {}), id: itemId, selected: false };
            selectedIds.delete(itemId);
          }
        }
      }

      Vue.set(this.state, 'selectedIds', selectedIds);
    },

    async fetchItems() {
      if (this.disabled) {
        return;
      }

      const { currentPage: page = 1, search = '' } = this.state;
      const payload = {
        search,
        page,
        /** @type {Record<string, any>} */
        filters: {},
        selected_only: this.state.selectedOnly,
        selection:
          this.asFilter && this.state.selectionType === 'SELECT_ALL'
            ? [...this.state.unselectedIds.values()]
            : this.state.selectionType === 'SELECT_ALL'
            ? []
            : [...this.state.selectedIds.values()],
        selection_type: this.state.selectionType.toLowerCase(),
        resource_id: this.resourceId,
      };

      this.state.loading = true;

      for (const filter of this.state.filters) {
        if (!filter.selected) {
          continue;
        }

        if (!payload.filters[filter.id]) {
          if (filter.multiple) {
            payload.filters[filter.id] = [];
          }
        }

        if (filter.multiple) {
          payload.filters[filter.id].push(filter.value);
        } else {
          payload.filters[filter.id] = filter.value;
        }
      }

      const finalPayload = Object.fromEntries(
        Object.entries(payload)
          .filter(([, value]) => value !== null)
          .map(([key, value]) => [key, value]),
      );

      try {
        /** @type {FetcherResponse} */
        const response = await this.fetcher(finalPayload);

        // If we don't have the required data in the response,
        // we just trigger the error and return.
        if (
          response.items &&
          response.pagination &&
          !response.selected_ids &&
          !response.unselected_ids &&
          !response.disabled_ids
        ) {
          /** @ts-ignore */
          this.$appMessage(`Unable to fetch data for the ${pluralize(2, this.resource)} select.`);

          return;
        }

        let items = response.items;

        if (this.unselectedOnly) {
          const initiallySelectedIds = this.state.initiallySelectedIds;

          items = items.filter((item) => !initiallySelectedIds.has(item.id));
        }

        // If we're in the first page it means that we can be in:
        // - First load
        // - Performing a search with new input
        // - Removing search completely
        //
        // In those cases we need to reset the whole list of items.
        if (this.state.currentPage === 1) {
          this.state.items = items;
        } else {
          // Create a new array with the current and response items so the virtual-list watcher notices the change
          this.state.items = [...this.state.items, ...items];
        }

        const selectedIds =
          this.state.selectionType === 'NORMAL'
            ? response.selected_ids
            : this.getSelectAll(new Set(response.selected_ids));

        if (!this.asFilter || (this.asFilter && !response.selected_ids.length)) {
          this.state.selectionType = 'NORMAL';
        }

        this.state.pagination = response.pagination;

        // This `if` block safeguards the client-side `unselectedIds` state when in 'SELECT_ALL' mode.
        // A data refetch (e.g., from applying a filter) can return a partial list of items from the server.
        // Recalculating `unselectedIds` from this incomplete list would incorrectly erase the user's
        // manual deselections. This guard prevents that by ensuring the local state is preserved,
        // treating it as the source of truth for deselections in this mode.
        if (this.state.selectionType !== 'SELECT_ALL') {
          const unselectedIds = this.getUnselected(
            new Set(response.items.map((item) => item.id)),
            new Set(selectedIds),
          );

          Vue.set(this.state, 'unselectedIds', new Set(unselectedIds));
        }

        Vue.set(this.state, 'selectedIds', new Set(selectedIds));
        Vue.set(this.state, 'disabledIds', new Set(response.disabled_ids));
        Vue.set(this.state, 'selectedDisabledIds', new Set(response.selected_disabled_ids));
      } catch (/** @type {any} */ fetchError) {
        if (![401, 403].includes(fetchError.status)) {
          /** @ts-ignore */
          this.$appMessage(fetchError?.responseJSON?.alert ?? 'An error has occurred.');
        }
      } finally {
        this.state.loading = false;
      }
    },
  },
});
</script>
